import { Box, Container, Typography, Card, CardContent, Grid } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useI18n } from '../../utils/i18n';
import useScrollReveal from '../../hooks/useScrollReveal';

const TESTIMONIALS = [
  { quoteKey: 'landing.testimonials.1.quote', nameKey: 'landing.testimonials.1.name', roleKey: 'landing.testimonials.1.role' },
  { quoteKey: 'landing.testimonials.2.quote', nameKey: 'landing.testimonials.2.name', roleKey: 'landing.testimonials.2.role' },
  { quoteKey: 'landing.testimonials.3.quote', nameKey: 'landing.testimonials.3.name', roleKey: 'landing.testimonials.3.role' },
];

function TestimonialCard({ quoteKey, nameKey, roleKey, index }) {
  const { t } = useI18n();
  const ref = useScrollReveal({ delay: index * 150 });

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Card
        ref={ref}
        sx={{
          height: '100%',
          bgcolor: 'background.paper',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 3,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(108,99,255,0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormatQuoteIcon
            sx={{ fontSize: 36, color: 'primary.main', opacity: 0.6 }}
          />
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', lineHeight: 1.7, fontStyle: 'italic', flexGrow: 1 }}
          >
            "{t(quoteKey)}"
          </Typography>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t(nameKey)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t(roleKey)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

/**
 * Testimonial section — 3 cards with quote, name, and role in a responsive grid
 * with scroll-reveal animation. Accepts `id` prop for anchor scrolling.
 */
export default function TestimonialSection({ id }) {
  const { t } = useI18n();
  const titleRef = useScrollReveal();

  return (
    <Box id={id} sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Typography
          ref={titleRef}
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          {t('landing.testimonials.title')}
        </Typography>

        <Grid container spacing={3}>
          {TESTIMONIALS.map((testimonial, index) => (
            <TestimonialCard key={testimonial.quoteKey} {...testimonial} index={index} />
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
